import { AnimalMedicalRecordChangeModel } from './animal-medical-record.model';
import {
  getMedicalConditionSeverityLabelKey,
  getPreventiveTreatmentCategoryLabelKey,
  getPreventiveTreatmentTypeLabelKey,
  getQuarantineReasonLabelKey,
  PreventiveTreatmentCategory,
  PreventiveTreatmentType
} from './medical.enums';
import { isoDatePart } from './medical.helpers';

/** Übersetzungsschlüssel oder fertiger Text – das Template entscheidet per `key`. */
export interface MedicalLabel {
  key?: string;
  text?: string;
}

/** Eine Feldänderung aus „Bestehendes fortschreiben“, fertig für einen Chip. */
export interface MedicalChangeChip {
  icon: string;
  target: MedicalLabel[];
  field: MedicalLabel;
  from: MedicalLabel;
  to: MedicalLabel;
}

type ChangeValue = string | boolean | null;

interface FieldFormat {
  labelKey: string;
  value: (value: ChangeValue) => MedicalLabel;
}

const EMPTY: MedicalLabel = { text: '–' };

function dateValue(emptyKey?: string): (value: ChangeValue) => MedicalLabel {
  return value => {
    const date = typeof value === 'string' ? isoDatePart(value) : null;
    if (!date) return emptyKey ? { key: emptyKey } : EMPTY;
    const [year, month, day] = date.split('-');
    return { text: `${day}.${month}.${year}` };
  };
}

const boolValue = (value: ChangeValue): MedicalLabel =>
  value === null ? EMPTY : { key: value === true || value === 'true' ? 'yes' : 'no' };

const FIELDS: Record<AnimalMedicalRecordChangeModel['targetType'], Record<string, FieldFormat>> = {
  condition: {
    resolvedAt: { labelKey: 'animalMedical.fields.resolvedAt', value: dateValue() },
    severity: {
      labelKey: 'animalMedical.fields.severity',
      value: value => typeof value === 'string' ? { key: getMedicalConditionSeverityLabelKey(value) } : EMPTY
    },
    visibleToAdopter: { labelKey: 'animalMedical.fields.visibleToAdopter', value: boolValue },
  },
  quarantine: {
    endsAt: { labelKey: 'animalMedical.fields.endsAt', value: dateValue('animalMedical.fields.unlimited') },
    blocksAdoption: { labelKey: 'animalMedical.fields.blocksAdoption', value: boolValue },
  },
  treatment: {
    nextDueAt: { labelKey: 'animalMedical.fields.nextDueAt', value: dateValue('animalMedical.fields.noFurtherDueShort') },
  },
};

const ICONS: Record<AnimalMedicalRecordChangeModel['targetType'], string> = {
  condition: 'pi pi-heart',
  quarantine: 'pi pi-lock',
  treatment: 'pi pi-shield',
};

const TYPES = new Set<string>(Object.values(PreventiveTreatmentType));
const CATEGORIES = new Set<string>(Object.values(PreventiveTreatmentCategory));

/** `targetLabel` laut Backend: Diagnose-Label, Quarantänegrund bzw. Prophylaxe-Typen (kommagetrennt) oder -Kategorie. */
function targetLabels(change: AnimalMedicalRecordChangeModel): MedicalLabel[] {
  const label = change.targetLabel?.trim();
  if (!label) return [{ text: `#${change.targetId}` }];

  switch (change.targetType) {
    case 'quarantine':
      return [{ key: getQuarantineReasonLabelKey(label) }];
    case 'treatment':
      return label.split(',').map(part => part.trim()).filter(Boolean).map(part =>
        TYPES.has(part) ? { key: getPreventiveTreatmentTypeLabelKey(part) }
          : CATEGORIES.has(part) ? { key: getPreventiveTreatmentCategoryLabelKey(part) }
          : { text: part }
      );
    default:
      return [{ text: label }];
  }
}

const rawValue = (value: ChangeValue): MedicalLabel => value === null || value === '' ? EMPTY : { text: String(value) };

/** Ein Chip je geändertem Feld; unbekannte Felder werden roh angezeigt. */
export function toChangeChips(changes: AnimalMedicalRecordChangeModel[] | null | undefined): MedicalChangeChip[] {
  return (changes ?? []).flatMap(change => {
    const target = targetLabels(change);
    return Object.entries(change.changes ?? {}).map(([field, diff]) => {
      const format = FIELDS[change.targetType]?.[field];
      return {
        icon: ICONS[change.targetType] ?? 'pi pi-pencil',
        target,
        field: format ? { key: format.labelKey } : { text: field },
        from: (format?.value ?? rawValue)(diff.from),
        to: (format?.value ?? rawValue)(diff.to),
      };
    });
  });
}
